import { AppContext, SessionContext, LanguageContext, PhotoUploadSession } from "../lib/contexts";
import { BrowserRouter } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { csCZ } from "@mui/material/locale";
import { CssBaseline, Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/cs";
import { SnackbarProvider } from "notistack";

import SpaPending from "./SpaPending";
import SpaUnauthenticated from "./SpaUnauthenticated";
import SpaError from "./SpaError";
import { ErrorBoundary } from "../lib/common";
import { useSession, useAppContext } from "../lib/hooks";

const mdTheme = createTheme({
  csCZ,
  palette: {
    primary: {
      main: "#c20c02",
    },
    secondary: {
      main: "#262626",
    },
  },
  components: {
    MuiTabs: {
      defaultProps: {
        variant: "scrollable",
        scrollButtons: "auto",
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
      },
    },
  },
});

function ProvidersFeedback({ children }) {
  const { session, call: sessionCall } = useSession();
  const appContext = useAppContext();

  const silentSession = sessionCall.feedback === "silent";
  const pendingAppContext = session.authenticated && ["silent", "pending"].includes(appContext.feedback);

  if (silentSession || pendingAppContext) return <SpaPending />;
  if (sessionCall.feedback === "error") return <SpaError error={sessionCall.response} />;
  if (appContext.feedback === "error") return <SpaError error={appContext.data} />;
  if (!session.authenticated) return <SpaUnauthenticated />;

  return children(true);
}

const GlobalCss = {
  display: "flex",
};

function AppProviders({ children }) {
  return (
    <Box sx={GlobalCss}>
      <CssBaseline />
      <ErrorBoundary spaLevel>
        <ThemeProvider theme={mdTheme}>
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "top", horizontal: "center" }} preventDuplicate>
            <SessionContext.SessionContextProvider>
              <AppContext.AppContextProvider>
                <BrowserRouter>
                  <LanguageContext.LanguageContextProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"cs"}>
                      <PhotoUploadSession.PhotoUploadSessionProvider>
                        <ProvidersFeedback>{(ready) => ready && children}</ProvidersFeedback>
                      </PhotoUploadSession.PhotoUploadSessionProvider>
                    </LocalizationProvider>
                  </LanguageContext.LanguageContextProvider>
                </BrowserRouter>
              </AppContext.AppContextProvider>
            </SessionContext.SessionContextProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Box>
  );
}

export default AppProviders;
